import { useEffect, useRef } from "react";
import { twMerge } from "tailwind-merge";
import { X } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { Button } from "@shared/ui/Button";

type SheetProps = {
  children: React.ReactNode;
  className?: string;
  innerClassName?: string;
  isOpen: boolean;
  onClose: () => void;
};

export function Sheet({
  children,
  className,
  innerClassName,
  isOpen,
  onClose,
}: SheetProps) {
  const ref = useRef<HTMLDivElement>(null);

  // Disable body scroll when dialog is open
  useEffect(() => {
    if (!isOpen) return;

    const originalOverflow = document.body.style.overflow;

    // Lock scroll
    document.body.style.overflow = "hidden";

    return () => {
      // Only restore if the dialog was the one to change it
      if (document.body.style.overflow === "hidden") {
        document.body.style.overflow = originalOverflow || "auto";
      }
    };
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => {
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  // Focus on the first focusable element when the sheet is opened
  useEffect(() => {
    if (isOpen && ref.current) {
      const focusableElements = ref.current.querySelectorAll<HTMLElement>(
        'button:not([data-ignore-focus]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      for (const el of focusableElements) {
        if (!el.closest("[sheet-close-button]")) {
          el.focus();
          break;
        }
      }
    }
  }, [isOpen]);

  const fadeDuration = 0.2;
  const slideOutDuration = 0.1;

  const backdropVariants = {
    enter: {
      opacity: 1,
      transition: { duration: fadeDuration, delay: 0 },
    },
    exit: {
      opacity: 0,
      transition: { duration: fadeDuration, delay: slideOutDuration },
    },
  };

  const panelVariants = {
    enter: {
      x: 0,
      transition: {
        duration: slideOutDuration,
        delay: fadeDuration,
        type: "linear",
      },
    },
    exit: {
      x: "100%",
      transition: { duration: slideOutDuration, delay: 0, type: "linear" },
    },
  };

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          variants={backdropVariants}
          initial="exit"
          animate="enter"
          exit="exit"
          className="fixed inset-0 z-50 bg-brand-900/90 backdrop-blur-[4px]"
          onClick={onClose}
        >
          <motion.div
            ref={ref}
            variants={panelVariants}
            initial="exit"
            animate="enter"
            exit="exit"
            className={twMerge(
              "absolute top-0 right-0 flex flex-col gap-4 bg-brand-50 h-full w-full max-w-xs justify-between",
              className
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute top-0 right-0 p-4">
              <Button
                variant="ghost"
                iconComponent={X}
                className="size-max"
                onClick={onClose}
                data-testid="sheet-close-button"
              />
            </div>
            <div
              className={twMerge(
                "flex flex-col justify-between h-full",
                innerClassName
              )}
            >
              {children}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default Sheet;
